import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import generateUUID from 'utils/sharedUtils/generateUUID';
import { useCreateAor, useUpdateAor } from 'hooks/useAors';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import StylishNewTextArea from 'components/DesignSystems/New/StylishNewTextArea';
import { toast } from 'react-toastify';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { DebounceInput } from 'react-debounce-input';
import { createLocationFromGeojson } from 'components/MapComponent/utils/createLocationFromGeojson';
import { addPolyLayer } from './mapEffects/geolocationsEffect';
import { useEnvironmentVariables } from '../../utils/useEnvironmentVariables';

export const samplePointSource = {
  id: '',
  type: 'geojson',
  data: {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [],
        },
        generateId: true,
      },
    ],
  },
};

export default function AorCreatePage({
  map,
  onClose,
  existingAOR = null,
  location = null,
  setLocation,
  drawRef,
  setLayerClicked,
  apiPrefix,
}) {
  const createAor = useCreateAor();
  const updateAor = useUpdateAor();

  const [isLoaded, setIsLoaded] = useState(true);

  const reduxCurrentlySelectedGroup = useSelector((state) => {
    return state.app.currentlySelectedGroup;
  });

  const [selectedLat, setSelectedLat] = useState(null);
  const [selectedLng, setSelectedLng] = useState(null);

  const [name, setName] = useState(
    (!!existingAOR && !!existingAOR.name && existingAOR.name) || ''
  );
  const [description, setDescription] = useState(
    (!!existingAOR && !!existingAOR.description && existingAOR.description) ||
      ''
  );

  const { data: envData = {} } = useEnvironmentVariables();
  const { MAPBOX_ACCESS_TOKEN: mapboxToken } = envData;

  const onChangeLatLang = (value, type) => {
    const geojson = { ...samplePointSource };
    if (type === 'lat') {
      if (value > 90 || value < -90) {
        toast.error('Latitude should be between -90 and 90');
        return;
      }
      setSelectedLat(value);
      if (
        existingAOR?.location?.centroid.geometry.coordinates[0] ||
        selectedLng
      ) {
        const val =
          existingAOR?.location?.centroid.geometry.coordinates[0] ||
          selectedLng;
        geojson.data.features[0].geometry.coordinates = [val, value];
        createLocationFromGeojson(
          geojson.data,
          mapboxToken,
          setLocation,
          setSelectedLat,
          setSelectedLng
        );
      }
    } else if (type === 'lng') {
      if (value > 180 || value < -180) {
        toast.error('Longitude should be between -180 and 180');
        return;
      }
      setSelectedLng(value);
      if (
        existingAOR?.location?.centroid.geometry.coordinates[1] ||
        selectedLat
      ) {
        const val =
          existingAOR?.location?.centroid.geometry.coordinates[1] ||
          selectedLat;
        geojson.data.features[0].geometry.coordinates = [value, val];
        createLocationFromGeojson(
          geojson.data,
          mapboxToken,
          setLocation,
          setSelectedLat,
          setSelectedLng
        );
      }
    }
  };

  useEffect(() => {
    if (!isLoaded && (createAor.isSuccess || updateAor.isSuccess)) {
      onClose();
    }
  }, [createAor.isSuccess, updateAor.isSuccess, isLoaded, onClose]);

  function createAOR() {
    const current_location_geom =
      !!location &&
      !!location.geojson &&
      location.geojson.data.features[0].geometry;
    const aor = {
      id: (!!existingAOR && existingAOR.id) || generateUUID(),
      group_guid: reduxCurrentlySelectedGroup.group_guid,
      name: name,
      description: description,
      location: location,
      current_location_geom: current_location_geom,
    };
    return aor;
  }

  const handleAORCreateClick = async () => {
    setIsLoaded(false);
    try {
      const aor = createAOR();
      if (existingAOR) {
        await updateAor.mutateAsync(aor);
      } else {
        await createAor.mutateAsync(aor);
      }

      drawRef.current.deleteAll();
      setLocation(null);
      drawRef.current.changeMode('simple_select');

      // Remove both layer and source
      if (map.current.getLayer('aors_polygons_layer')) {
        map.current.removeLayer('aors_polygons_layer');
      }
      if (map.current.getSource('geolocation_tiles_aors_polygons_source')) {
        map.current.removeSource('geolocation_tiles_aors_polygons_source');
      }

      // Add a small delay to ensure everything is properly removed before adding back
      setTimeout(() => {
        // Re-add the source first with correct URL format
        map.current.addSource('geolocation_tiles_aors_polygons_source', {
          type: 'vector',
          tiles: [
            `${apiPrefix}/geolocations_tiles/public.geolocation_tiles_aors_polygons/{z}/{x}/{y}.pbf?user_group_guid=${reduxCurrentlySelectedGroup.group_guid}`,
          ],
          minzoom: 0,
          maxzoom: 22,
        });

        // Then add the layer
        addPolyLayer(
          map,
          'aors',
          'geolocation_tiles_aors_polygons_source',
          'public.geolocation_tiles_aors_polygons',
          setLayerClicked
        );
      }, 500);
    } catch (error) {
      console.error('Error during upsert:', error);
    } finally {
      setIsLoaded(true);
      onClose();
    }
  };

  const currName = (!!existingAOR && existingAOR.name) || null;

  return (
    <div className="aor-create-page">
      <div className="aor-create-page__header">
        <h2>
          {(!!existingAOR && <>Edit {existingAOR.name}</>) || <>New AOR</>}
        </h2>
      </div>
      <div className="aor-create-page__body">
        <div className="mb-3">
          <label className="form-label">Name</label>
          <StylishNewInput
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Description</label>
          <StylishNewTextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Location</label>
          <div className="location-inputs">
            <div className="location-input">
              <label>Latitude</label>
              <DebounceInput
                className="form-control"
                value={selectedLat || ''}
                onChange={(e) => onChangeLatLang(e.target.value, 'lat')}
                debounceTimeout={500}
                placeholder="Enter latitude"
              />
            </div>
            <div className="location-input">
              <label>Longitude</label>
              <DebounceInput
                className="form-control"
                value={selectedLng || ''}
                onChange={(e) => onChangeLatLang(e.target.value, 'lng')}
                debounceTimeout={500}
                placeholder="Enter longitude"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="aor-create-page__footer">
        <div className="button-group">
          <StylishNewButton
            type="button"
            className="button--secondary button--reverse"
            onClick={onClose}
          >
            Cancel
          </StylishNewButton>
          <StylishNewButton
            className="button--primary"
            disabled={!isLoaded || !name}
            onClick={() => handleAORCreateClick()}
          >
            Save {currName || 'AOR'}
          </StylishNewButton>
        </div>
      </div>
      {!isLoaded && <div>loading...</div>}
    </div>
  );
}
